import axios from "@/plugins/customAxios";
import SlotTag from "@/types/SlotTag";

export default class SlotTagService {

  /**
   * Get all available slot tags
   * Results are cached after first fetch
   */
  public static async getAll(): Promise<SlotTag[]> {
    if (this.cachedTags !== null) {
      return this.cachedTags as SlotTag[];
    }

    const response = await axios.get(this.basePath);
    this.cachedTags = response.data;
    return this.cachedTags as SlotTag[];
  }

  /**
   * Clear the cached tags (useful after updates)
   */
  public static clearCache(): void {
    this.cachedTags = null;
  }

  /**
   * Get slot tag by name
   */
  public static async getByName(name: string): Promise<SlotTag | undefined> {
    const tags = await this.getAll();
    return tags.find((tag) => tag.name === name);
  }

  /**
   * Check if a slot has a specific tag
   */
  public static hasTag(slotTags: SlotTag[] | undefined, tagName: string): boolean {
    if (!slotTags) { return false; }
    return slotTags.some((tag) => tag.name === tagName);
  }

  /**
   * Check if a slot has any leadership tag
   */
  public static isLeadershipRole(slotTags: SlotTag[] | undefined): boolean {
    return this.hasTag(slotTags, "Leadership") || this.hasTag(slotTags, "HQ");
  }

  /**
   * Check if a slot is a combat role
   */
  public static isCombatRole(slotTags: SlotTag[] | undefined): boolean {
    if (!slotTags) { return false; }
    const combatTags = ["Anti Air", "Anti Tank", "Automatic Rifleman", "Marksman", "Recon", "Specialist"];
    return slotTags.some((tag) => combatTags.includes(tag.name));
  }

  /**
   * Check if a slot is a support role
   */
  public static isSupportRole(slotTags: SlotTag[] | undefined): boolean {
    if (!slotTags) { return false; }
    const supportTags = ["Medic", "Engineer", "Logistics", "Fire Support", "Assistant"];
    return slotTags.some((tag) => supportTags.includes(tag.name));
  }

  /**
   * Get tag color for UI display
   */
  public static getTagColor(tagName: string): string {
    return "primary"; // Placeholder for future customization
  }

  /**
   * Get tag icon for UI display
   */
  public static getTagIcon(tagName: string): string {
    const iconMap: Record<string, string> = {
      "Leadership": "mdi-account-star",
      "HQ": "mdi-office-building",
      "Medic": "mdi-medical-bag",
      "Engineer": "mdi-wrench",
      "Anti Tank": "mdi-tank",
      "Anti Air": "mdi-airplane",
      "Automatic Rifleman": "mdi-ammunition",
      "Marksman": "mdi-crosshairs",
      "Recon": "mdi-binoculars",
      "Specialist": "mdi-star-circle",
      "Pilot": "mdi-helicopter",
      "Crew": "mdi-car-side",
      "Fire Support": "mdi-fire",
      "Logistics": "mdi-package-variant",
      "Assistant": "mdi-account-arrow-right",
      "GM": "mdi-chess-king",
    };
    return iconMap[tagName] || "mdi-tag";
  }
  private static basePath = "/arma/slottags";
  private static cachedTags: SlotTag[] | null = null;
}
